import React, { useRef, useState } from 'react';

import { Box } from '@rbilabs/universal-components';
import { CameraType, CameraView, FlashMode } from 'expo-camera';
import { Platform, StyleSheet, View, useWindowDimensions } from 'react-native';

import ActionButton from 'components/action-button';
import { t } from 'state/intl/translations';
import { primitive } from 'styles/constants';

import {
  CameraIcons,
  CameraInstructionPopup,
  CameraInstructionText,
  CustomButton,
  ImageOverlay,
  ImageOverlayContainer,
  PromptText,
  PromptTextContainer,
  TopBar,
} from './styled';
import { CameraScreenProps, FlipMode } from './types';
import { getNormalizedImage } from './utils';

export const CameraScreen = ({
  flip = FlipMode.Front,
  onTakePhoto,
  onClose,
  promptText,
  flippable = false,
  imageOverlay = '',
  textInstructions = '',
  isCameraReady = false,
}: CameraScreenProps) => {
  const { width, height } = useWindowDimensions();
  const [flash, setFlash] = useState('off');
  const cameraRef = useRef<CameraView>(null);
  const isWeb = Platform.OS === 'web';
  const [type, setType] = useState<CameraType>(flip as CameraType);

  const stopWebCameras = async () => {
    // Safari does not support stopping the camera stream,
    // so we need to stop it manually if it's running.
    // This is a workaround for Safari's camera handling. but also works for other browsers.
    // This is necessary because Safari does not allow multiple camera streams to be active at the same time.
    // If the camera is already stopped, this will not throw an error.
    if (isWeb) {
      // Stop all active camera streams
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream?.getTracks()?.forEach(track => track?.stop());
    }
  };

  const closeCamera = async () => {
    await stopWebCameras();
    onClose();
  };

  const takePhoto = async () => {
    if (cameraRef.current) {
      const photo = await cameraRef.current.takePictureAsync({
        base64: true,
      });
      stopWebCameras();
      onTakePhoto({
        base64: photo?.base64,
        uri: photo?.uri,
        width: photo?.width || 0,
        height: photo?.height || 0,
      });
    }
  };

  // Percentage does not work because of the absolute position that
  // is configured because of the iframe of the campaign
  // so we calculate it based in the percentage:
  // top header : 5% = 0.05
  // body : 80% = 0.8
  // bottom header : 15% = 0.15
  // Max width is 500px for web only
  const BODY_HEIGHT = height * 0.8;
  const BOTTOM_HEIGHT = height * 0.15;
  const TOP_BAR_HEIGHT = height * 0.05;
  const MAX_WIDTH = 500;

  return (
    <>
      <TopBar style={{ height: TOP_BAR_HEIGHT, width, maxWidth: MAX_WIDTH }}>
        <CustomButton onPress={closeCamera}>
          <CameraIcons name="close" />
        </CustomButton>
        {!isWeb && flippable && (
          <Box flexDirection="row" alignItems="center" justifyContent="center">
            <CustomButton
              onPress={() => {
                setType(type === 'back' ? 'front' : 'back');
              }}
            >
              <CameraIcons name="camera-reverse" />
            </CustomButton>

            <CustomButton onPress={() => setFlash(flash === 'off' ? 'on' : 'off')}>
              <CameraIcons name={flash === 'off' ? 'flash-off-outline' : 'flash-outline'} />
            </CustomButton>
          </Box>
        )}
      </TopBar>
      <Box
        style={[
          { height: BODY_HEIGHT, width, maxWidth: MAX_WIDTH, backgroundColor: primitive.$black },
        ]}
      >
        {isCameraReady && (
          <CameraView
            ref={cameraRef}
            facing={type}
            flash={flash as FlashMode}
            style={{ width, height: BODY_HEIGHT, maxWidth: MAX_WIDTH }}
          />
        )}
        {promptText && (
          <PromptTextContainer>
            <PromptText>{promptText}</PromptText>
          </PromptTextContainer>
        )}
        {imageOverlay && (
          <ImageOverlayContainer
            style={{
              width,
              maxWidth: MAX_WIDTH,
              height: BODY_HEIGHT,
            }}
          >
            <ImageOverlay alt="Image overlay" source={{ uri: getNormalizedImage(imageOverlay) }} />
          </ImageOverlayContainer>
        )}
        {isCameraReady && textInstructions && (
          <CameraInstructionPopup>
            <CameraInstructionText>{textInstructions}</CameraInstructionText>
          </CameraInstructionPopup>
        )}
        {!isCameraReady && (
          <CameraInstructionPopup>
            <CameraInstructionText>{t('waitingForCamera')}</CameraInstructionText>
          </CameraInstructionPopup>
        )}
      </Box>
      <Box
        alignItems="center"
        justifyContent="center"
        style={{
          height: BOTTOM_HEIGHT,
          width,
          maxWidth: MAX_WIDTH,
          backgroundColor: primitive.$black,
        }}
      >
        <ActionButton style={styles.captureBtn} onPress={takePhoto}>
          <View style={styles.captureBtnInner} />
        </ActionButton>
      </Box>
    </>
  );
};

const styles = StyleSheet.create({
  captureBtn: {
    width: 54,
    height: 54,
    borderRadius: 37,
    borderWidth: 4,
    borderColor: primitive.$white,
    backgroundColor: primitive.$black,
    bottom: 20,
  },
  captureBtnInner: {
    width: 36,
    height: 36,
    borderRadius: 28,
    backgroundColor: primitive.$white,
  },
});
